import React from 'react';
import SEO from '../components/SEO';
import UpworkReviews from '../components/UpworkReviews';
import CTABanner from '../components/CTABanner';
import Footer from '../components/Footer';
import { caseStudies } from '../mock';
import { ArrowUpRight, Quote } from 'lucide-react';

const CaseStudies = () => {
  const featured = caseStudies[0];
  const rest = caseStudies.slice(1);
  return (
    <>
      <SEO
        title="Case Studies — Real Email Outcomes | Growth Hacking"
        description="Real client results from migrations, deliverability rescues and newsletter monetization — with the numbers that matter."
        canonical="/case-studies"
      />
      <main className="bg-cream pt-28">
        {/* Hero */}
        <section className="container-x section pt-12" data-testid="cases-hero">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cream-2 border border-line text-xs font-semibold uppercase tracking-[0.2em] text-ink">
                Case studies
              </span>
              <h1 className="font-display text-5xl md:text-7xl font-extrabold mt-6 leading-[0.98] text-ink tracking-tighter">
                Numbers we are{' '}
                <span className="font-serif italic text-[var(--gh-orange)]">proud</span> of.
              </h1>
            </div>
            <p className="lg:col-span-4 text-lg text-muted-warm">
              Each project below ties a clear before/after to a specific email lever we
              pulled.
            </p>
          </div>
        </section>

        {/* Featured case */}
        <section className="container-x section pt-0" data-testid="featured-case">
          <div className="grid lg:grid-cols-12 gap-8 items-stretch">
            <div className="lg:col-span-7 rounded-[1.75rem] overflow-hidden bg-cream-2 border border-line">
              <img src={featured.image} alt={featured.clientName} className="w-full h-[420px] object-cover" />
            </div>
            <div className="lg:col-span-5 rounded-[1.75rem] bg-ink text-[var(--gh-cream)] p-10 flex flex-col justify-between">
              <div>
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-[10px] uppercase tracking-widest font-semibold">
                  Featured · {featured.badge}
                </span>
                <h2 className="font-display text-3xl md:text-4xl font-extrabold mt-6 leading-tight">
                  {featured.tagline}
                </h2>
                <p className="mt-4 text-white/75 text-sm leading-relaxed">{featured.fullDescription}</p>
              </div>
              <div className="mt-8 flex items-center justify-between gap-4">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-white/55">Client</p>
                  <p className="font-bold">{featured.clientName}</p>
                </div>
                <a href="/contact" className="btn-orange" data-testid="featured-case-cta">
                  Run mine <ArrowUpRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Rest in grid */}
        <section className="container-x section pt-0" data-testid="case-grid">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {rest.map((c) => (
              <article
                key={c.id}
                data-testid={`case-card-${c.id}`}
                className="card-cream overflow-hidden flex flex-col"
              >
                <img src={c.image} alt={c.clientName} className="w-full h-56 object-cover" />
                <div className="p-7 flex flex-col flex-1">
                  <span className="inline-flex w-fit items-center gap-2 px-3 py-1 rounded-full bg-[var(--gh-orange-soft)] text-[var(--gh-orange)] text-[11px] font-semibold uppercase tracking-widest">
                    {c.badge}
                  </span>
                  <h3 className="font-display text-2xl font-bold mt-5 text-ink leading-tight">{c.tagline}</h3>
                  <p className="mt-3 text-sm text-muted-warm leading-relaxed flex-1">{c.fullDescription}</p>
                  <p className="mt-5 text-[11px] uppercase tracking-widest text-muted-warm font-semibold">
                    Client · {c.clientName}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* Pull-quote band */}
        <section className="container-x section" data-testid="cases-quote">
          <div className="rounded-[1.75rem] bg-cream-2 border border-line p-10 md:p-16 relative overflow-hidden">
            <Quote className="w-10 h-10 text-[var(--gh-orange)]" />
            <p className="mt-6 font-display text-3xl md:text-4xl font-extrabold text-ink leading-snug max-w-4xl">
              “Akash quietly rebuilt our newsletter, and within five months our open rate
              jumped from 17% to 87%. He treats your inbox like it&apos;s his own.”
            </p>
            <p className="mt-6 text-sm uppercase tracking-widest text-muted-warm font-semibold">
              Amber · The Well Drop
            </p>
            <div
              aria-hidden
              className="absolute -bottom-20 -right-20 w-60 h-60 rounded-full bg-[var(--gh-orange)] opacity-10 blur-3xl"
            />
          </div>
        </section>

        <UpworkReviews />
        <CTABanner
          eyebrow="Your turn"
          title="Want results like these on your list?"
          body="Send me your numbers — list size, ESP, last-30-day open rate — and I'll come back with three things I'd ship in your first week."
        />
      </main>
      <Footer />
    </>
  );
};

export default CaseStudies;
